import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMotionValue, type MotionValue } from 'framer-motion';
import { useLayoutMotionValueEvent } from '../hooks/useLayoutMotionValueEvent';
import type {
  AudioBands,
  LyricData,
  Theme,
  VisualizerMode,
} from '../types';
import type { VisualizerSharedProps } from '../components/visualizer/definition';
import type { VisualizerBackgroundConfig } from '../components/visualizer/backgrounds/definition';
import type { VisualizerTuningBundle } from '../components/visualizer/tuningRegistry';
import VisualizerRenderer from '../components/visualizer/VisualizerRenderer';
import { DEFAULT_VISUALIZER_MODE } from '../components/visualizer/registry';
import { findLatestActiveLineIndex } from '../lyrics/findActiveLine';
import { createEmptyAudioBands } from './createAudioBands';

// packages/lyric-stage/src/host/LyricStage.tsx
// Host adapter over Folia's VisualizerRenderer (all modes + backgrounds).

export interface LyricStageProps extends Partial<Omit<VisualizerSharedProps, 'currentTime' | 'currentLineIndex' | 'lines' | 'theme' | 'audioPower' | 'audioBands'>> {
  lyrics: LyricData;
  theme: Theme;
  mode?: VisualizerMode;
  currentTime?: MotionValue<number>;
  currentTimeSec?: number;
  currentLineIndex?: number;
  audioPower?: MotionValue<number>;
  audioBands?: AudioBands;
  background?: VisualizerBackgroundConfig;
  visualizerTunings?: VisualizerTuningBundle;
  className?: string;
  style?: React.CSSProperties;
}

const LyricStage: React.FC<LyricStageProps> = ({
  lyrics,
  theme,
  mode = DEFAULT_VISUALIZER_MODE,
  currentTime: currentTimeProp,
  currentTimeSec,
  currentLineIndex: currentLineIndexProp,
  audioPower: audioPowerProp,
  audioBands: audioBandsProp,
  background,
  className,
  style,
  songTitle,
  songArtist,
  ...shared
}) => {
  const fallbackTime = useMotionValue(0);
  const currentTime = currentTimeProp ?? fallbackTime;

  useEffect(() => {
    if (currentTimeProp || currentTimeSec === undefined) {
      return;
    }
    fallbackTime.set(currentTimeSec);
  }, [currentTimeProp, currentTimeSec, fallbackTime]);

  const emptySignals = useMemo(() => createEmptyAudioBands(), []);
  const audioPower = audioPowerProp ?? emptySignals.audioPower;
  const audioBands = audioBandsProp ?? emptySignals.audioBands;

  const [derivedLineIndex, setDerivedLineIndex] = useState(() =>
    findLatestActiveLineIndex(lyrics.lines, currentTime.get()),
  );
  const linesRef = useRef(lyrics.lines);
  linesRef.current = lyrics.lines;
  const currentLineIndexPropRef = useRef(currentLineIndexProp);
  currentLineIndexPropRef.current = currentLineIndexProp;

  useLayoutMotionValueEvent(currentTime, 'change', (latest) => {
    if (currentLineIndexPropRef.current !== undefined) {
      return;
    }
    const next = findLatestActiveLineIndex(linesRef.current, latest);
    setDerivedLineIndex(prev => (prev === next ? prev : next));
  });

  useEffect(() => {
    if (currentLineIndexProp !== undefined) {
      return;
    }
    const next = findLatestActiveLineIndex(lyrics.lines, currentTime.get());
    setDerivedLineIndex(prev => (prev === next ? prev : next));
  }, [currentLineIndexProp, currentTime, lyrics.lines]);

  const currentLineIndex = currentLineIndexProp ?? derivedLineIndex;

  return (
    <div className={className} style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
      <VisualizerRenderer
        mode={mode}
        currentTime={currentTime}
        currentLineIndex={currentLineIndex}
        lines={lyrics.lines}
        theme={theme}
        audioPower={audioPower}
        audioBands={audioBands}
        songTitle={songTitle ?? lyrics.title}
        songArtist={songArtist ?? lyrics.artist}
        background={background}
        {...shared}
      />
    </div>
  );
};

export default LyricStage;
